import { API_BASE_URL, apiRequest, getCurrentAccessToken } from '@/shared/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/**
 * Preferências do usuário sincronizadas entre devices via DB.
 * Mantém o mesmo shape do `UserPreferences` no backend (`@chatplus/db`).
 */
export interface UserPreferences {
  signatureEnabled?: boolean;
  theme?: 'light' | 'dark';
  /** Disponibilidade manual: 'offline' = não recebe distribuição automática. */
  availability?: 'online' | 'offline';
  /** Somente-leitura (controlado pelo gestor, fora do PATCH /me): quando FALSE,
   *  o atendente não pode desativar a assinatura (composer trava o botão). */
  allowDisableSignature?: boolean;
  /** Conversas fixadas no topo da aba Ativos (pessoal, padrão WhatsApp). */
  pinnedTicketIds?: number[];
  /** Ordenação escolhida na lista do Atendimento (ausente = 'ultima-mensagem'). */
  inboxOrder?: InboxOrder;
}

/** Ordenação da lista do Atendimento (faixa cinza, ícone de ordenar). */
export type InboxOrder =
  | 'ultima-mensagem'
  | 'chegada-novos-primeiro'
  | 'chegada-novos-final'
  | 'nao-lidas';

const QUERY_KEY = ['me', 'preferences'] as const;

/**
 * Hook que retorna as prefs do usuário logado.
 * Cache de 5min — preferences mudam raramente, evita request a cada render.
 */
/**
 * Só LÊ o que já está em cache, sem nunca disparar busca.
 *
 * Pra quem remonta o tempo todo — hoje o Composer, que é reconstruído a cada
 * troca de conversa. Cada remontagem cria um observador novo, e observador novo
 * com dado vencido REBUSCA: virava uma requisição a cada 30s de uso
 * (~120/h por atendente, ~2.400/h numa frota de 20) pra um dado que muda uma ou
 * duas vezes por dia. Apareceu no rastro de ações, atribuído a rolagem e a
 * troca de conversa.
 *
 * A frescura continua garantida por quem NÃO remonta — `AppShell` e `Topbar`
 * mantêm o `useUserPreferences` montado a sessão inteira, então o F5 e o
 * vencimento seguem cobertos por eles. Aqui a gente só aproveita a carona.
 */
export function useUserPreferencesValue() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiRequest<UserPreferences>('/me/preferences'),
    staleTime: 30_000,
    // O par que faz a diferença: montar não busca, e sem dado nenhum também
    // não busca (quem busca é o AppShell). Este aqui é leitor, não pedinte.
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useUserPreferences() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiRequest<UserPreferences>('/me/preferences'),
    // O que precisa continuar valendo: availability/signature têm que refletir o
    // BANCO depois de um F5. Com cache persistido em localStorage, o reload
    // mostrava valor velho — ex: marca offline no celular, F5 na web voltava a
    // mostrar online. Isso importa porque `availability` alimenta a distribuição
    // automática: exibir errado faz o atendente re-clicar e INVERTER o estado real.
    //
    // Antes: staleTime 0 + refetchOnMount 'always'. O 'always' ignora o
    // staleTime e re-busca em TODA montagem — e o Composer remonta a cada troca
    // de conversa, então virava uma requisição por conversa aberta (~3k/h na
    // frota) pra um dado que muda uma ou duas vezes por dia.
    //
    // Agora: refetchOnMount padrão (só re-busca se estiver velho) + 30s de
    // validade. O F5 continua coberto (após um reload o dado quase sempre passou
    // dos 30s → re-busca), mas trocar de conversa dentro da janela usa o cache.
    staleTime: 30_000,
    refetchOnMount: true,
    // Voltar o FOCO pra aba já rebusca (padrão do TanStack, mantido de
    // propósito: é exatamente quando o atendente precisa do estado certo).
    // O gatilho de "rede reconectou" dispara junto nesse mesmo momento — o
    // navegador acorda, avisa que voltou, e a aba ganha foco — e como não são
    // simultâneos, viravam DUAS buscas do mesmo dado. Um basta, e o foco é o
    // que descreve melhor a situação: o atendente está olhando.
    refetchOnReconnect: false,
  });
}

/**
 * Mutation pra atualizar prefs (PATCH — merge superficial no backend).
 * Após sucesso, atualiza o cache do `useUserPreferences` com a resposta nova.
 */
export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<UserPreferences>) =>
      apiRequest<UserPreferences>('/me/preferences', { method: 'PATCH', body: patch }),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
    },
  });
}

/**
 * Fixa/desafixa uma conversa na aba Ativos (toggle em `pinnedTicketIds`).
 *
 * Otimista: atualiza o cache ANTES do PATCH — a lista reordena na hora
 * (fixar precisa parecer instantâneo, igual WhatsApp). Erro no servidor →
 * invalidate re-busca a verdade e desfaz. Cap 50 (mesmo limite do backend):
 * ao estourar, descarta os fixados mais antigos.
 */
export function useTogglePinnedTicket() {
  const qc = useQueryClient();
  const mut = useUpdatePreferences();
  return (ticketId: number): void => {
    const current = qc.getQueryData<UserPreferences>(QUERY_KEY)?.pinnedTicketIds ?? [];
    const next = current.includes(ticketId)
      ? current.filter((id) => id !== ticketId)
      : [...current, ticketId].slice(-50);
    qc.setQueryData<UserPreferences>(QUERY_KEY, (old) => ({
      ...(old ?? {}),
      pinnedTicketIds: next,
    }));
    mut.mutate(
      { pinnedTicketIds: next },
      { onError: () => void qc.invalidateQueries({ queryKey: QUERY_KEY }) },
    );
  };
}

/** Quando esta página carregou — separa a preferência buscada AGORA da que
 *  veio guardada no navegador (que pode ter mudado em outro computador). */
const PAGINA_CARREGOU_EM = Date.now();
/** Guardada no navegador há menos que isso ainda vale: é a validade do
 *  `useUserPreferences` (staleTime) — dentro dela, ninguém re-busca. */
const PREFS_VALIDADE_MS = 30_000;
/** Servidor lento/fora: depois disso a lista carrega com o que houver. */
const PREFS_ESPERA_MAXIMA_MS = 3_000;

/**
 * Ordenação da lista do Atendimento, LEMBRADA por usuário (dono, 18/out/2026):
 * vale depois do F5 e em outro computador. Quem nunca escolheu fica em
 * "Última mensagem mais recente primeiro" (igual ao WhatsApp — sem atrito).
 * Otimista como o fixar: a lista troca na hora; erro no PATCH desfaz.
 *
 * `pronta`: a ordem já veio do SERVIDOR nesta abertura (ou a guardada no
 * navegador é recente). A lista espera por ela antes de buscar — senão abria
 * na ordem padrão/antiga e "pulava" pra escolhida um instante depois (ex.:
 * escolheu em outro computador). Erro ou demora de 3 s → segue sem esperar.
 */
export function useInboxOrder(): [InboxOrder, (o: InboxOrder) => void, boolean] {
  const qc = useQueryClient();
  const mut = useUpdatePreferences();
  const prefsQ = useUserPreferencesValue();
  const ordem = prefsQ.data?.inboxOrder ?? 'ultima-mensagem';
  const doServidor =
    prefsQ.isSuccess && prefsQ.dataUpdatedAt >= PAGINA_CARREGOU_EM - PREFS_VALIDADE_MS;
  const [desistiu, setDesistiu] = useState(false);
  useEffect(() => {
    if (doServidor || prefsQ.isError) return;
    const id = setTimeout(() => setDesistiu(true), PREFS_ESPERA_MAXIMA_MS);
    return () => clearTimeout(id);
  }, [doServidor, prefsQ.isError]);
  const pronta = doServidor || prefsQ.isError || desistiu;
  const escolher = (o: InboxOrder): void => {
    qc.setQueryData<UserPreferences>(QUERY_KEY, (old) => ({ ...(old ?? {}), inboxOrder: o }));
    mut.mutate(
      { inboxOrder: o },
      { onError: () => void qc.invalidateQueries({ queryKey: QUERY_KEY }) },
    );
  };
  return [ordem, escolher, pronta];
}

const AVATAR_KEY = ['me', 'avatar'] as const;

/** URL (HMAC, estável) da foto de avatar do user atual — null se sem foto. */
export function useMyAvatar() {
  return useQuery({
    queryKey: AVATAR_KEY,
    queryFn: () => apiRequest<{ avatar_url: string | null }>('/me/avatar'),
    staleTime: 5 * 60_000,
  });
}

/** Upload da própria foto (multipart). Atualiza o cache do avatar no sucesso. */
export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: Blob): Promise<{ avatar_url: string | null }> => {
      const fd = new FormData();
      fd.append('file', file, file instanceof File ? file.name : 'avatar');
      const token = getCurrentAccessToken();
      const res = await fetch(`${API_BASE_URL}/me/avatar`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        ...(token && { headers: { authorization: `Bearer ${token}` } }),
      });
      if (!res.ok) throw new Error((await res.text()) || `upload failed (${res.status})`);
      return res.json() as Promise<{ avatar_url: string | null }>;
    },
    onSuccess: (data) => {
      qc.setQueryData(AVATAR_KEY, data);
      // A lista de Usuários (Configurações) mostra avatares via ['settings-users'].
      // Invalida pra a própria foto aparecer lá sem esperar o poll de 60s.
      void qc.invalidateQueries({ queryKey: ['settings-users'] });
    },
  });
}
